// Time window utilities for video access control
export interface TimeWindow {
  label: 'morning' | 'evening' | 'custom';
  from: string; // HH:MM format
  to: string; // HH:MM format
}

// Helper to get local date string in YYYY-MM-DD format
export function getLocalDateString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isWithinWindow(dateISO: string, windows: TimeWindow[]): { allowed: boolean; nextWindow?: Date; currentWindow?: TimeWindow } {
  const now = new Date();
  
  // Use local dates for comparison
  const nowDay = getLocalDateString(now);
  const targetDay = dateISO; // Already in YYYY-MM-DD format
  
  if (nowDay !== targetDay) {
    // Not today - find next window
    const validWindows = windows.filter(w => {
      const [fromH, fromM] = w.from.split(':').map(Number);
      const [toH, toM] = w.to.split(':').map(Number);
      return (toH * 60 + toM) > (fromH * 60 + fromM);
    }).sort((a, b) => a.from.localeCompare(b.from));
    
    const firstWindow = validWindows[0];
    if (firstWindow) {
      const [year, month, day] = targetDay.split('-').map(Number);
      const [hour, minute] = firstWindow.from.split(':').map(Number);
      const nextStart = new Date(year, month - 1, day, hour, minute);
      return { allowed: false, nextWindow: nextStart };
    }
    return { allowed: false };
  }

  // Filter out invalid windows and sort by start time
  const validWindows = windows.filter(w => {
    const [fromH, fromM] = w.from.split(':').map(Number);
    const [toH, toM] = w.to.split(':').map(Number);
    return (toH * 60 + toM) > (fromH * 60 + fromM);
  }).sort((a, b) => a.from.localeCompare(b.from));

  // Check each valid window using local time
  const [year, month, day] = targetDay.split('-').map(Number);
  
  for (const window of validWindows) {
    const [fromH, fromM] = window.from.split(':').map(Number);
    const [toH, toM] = window.to.split(':').map(Number);
    
    const start = new Date(year, month - 1, day, fromH, fromM);
    const end = new Date(year, month - 1, day, toH, toM);
    
    if (now >= start && now <= end) {
      return { allowed: true, currentWindow: window };
    }
    
    // If current time is before this window, it's the next one
    if (now < start) {
      return { allowed: false, nextWindow: start };
    }
  }

  // All valid windows passed for today
  return { allowed: false };
}

export function formatCountdown(targetDate: Date): string {
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();
  
  if (diff <= 0) return '0 મિનિટ';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours} કલાક ${minutes} મિનિટ`;
  }
  return `${minutes} મિનિટ`;
}

export function getWindowLabel(label: string): string {
  const labels: Record<string, string> = {
    morning: 'સવારે',
    evening: 'સાંજે',
    custom: 'વિશેષ સમય',
  };
  return labels[label] || label;
}
